import { apiClient } from './api';

/**
 * Facing detection response from backend
 */
export interface FacingDetectionResponse {
  video_id: string;
  direction: 'left' | 'right';
  confidence: number;
  method: string;
  details?: {
    shoulder_vote: string;
    nose_offset_vote: string | null;
    frames_analyzed: number;
  };
}

/**
 * Backend API response structure
 */
interface BackendFacingResponse {
  video_id: string;
  direction: string;
  confidence: number;
  method: string;
  frames_analyzed: number;
  details: {
    shoulder_direction_votes: { left: number; right: number };
    nose_offset_votes: { left: number; right: number };
  };
}

/**
 * Detect facing direction for a video
 *
 * Uses the dedicated facing direction API endpoint that analyzes
 * multiple frames using pose landmark data to determine which
 * direction the person is facing.
 *
 * @param videoId - The video ID (filename with extension)
 * @returns Facing detection response with direction and confidence
 */
export async function detectFacingDirection(
  videoId: string
): Promise<FacingDetectionResponse> {
  const response = await apiClient.get<BackendFacingResponse>(
    `/videos/${encodeURIComponent(videoId)}/facing-direction`
  );

  const data = response.data;

  // Map backend direction to frontend type
  const direction = data.direction === 'left' || data.direction === 'right'
    ? data.direction
    : 'right'; // Default to right if unknown

  return {
    video_id: data.video_id,
    direction,
    confidence: data.confidence,
    method: data.method,
    details: {
      shoulder_vote: data.details?.shoulder_direction_votes
        ? (data.details.shoulder_direction_votes.left > data.details.shoulder_direction_votes.right ? 'left' : 'right')
        : direction,
      nose_offset_vote: data.details?.nose_offset_votes
        ? (data.details.nose_offset_votes.left > data.details.nose_offset_votes.right ? 'left' : 'right')
        : null,
      frames_analyzed: data.frames_analyzed,
    },
  };
}

/**
 * Manually set facing direction override
 *
 * This doesn't call the backend but returns a response with 100% confidence
 * for manual selection scenarios.
 *
 * @param videoId - The video ID
 * @param direction - The manually selected direction
 * @returns Facing detection response with 100% confidence
 */
export function createManualFacingOverride(
  videoId: string,
  direction: 'left' | 'right'
): FacingDetectionResponse {
  return {
    video_id: videoId,
    direction,
    confidence: 1.0,
    method: 'manual',
    details: {
      shoulder_vote: direction,
      nose_offset_vote: null,
      frames_analyzed: 0,
    },
  };
}
